import { differenceInSeconds } from 'date-fns';
import { Task } from '../types/task';
import { UpdateTaskData } from '../types/api';

/**
 * 任务拖拽处理工具
 * 提供统一的拖拽逻辑，支持整体拖拽和调整大小
 */

export interface DragResult {
  updateData: UpdateTaskData;
  shouldRevert: boolean;
  message?: string;
}

/**
 * 处理任务整体拖拽（移动开始时间）
 * @param task 原始任务
 * @param newStartTime 新的开始时间
 * @returns 拖拽处理结果
 */
export const handleTaskDrag = (task: Task, newStartTime: Date): DragResult => {
  const updateData: UpdateTaskData = {
    id: task.id,
    startAt: newStartTime.toISOString(),
  };

  // 检查任务原本是否有 cost 值
  const hadOriginalCost = task.cost !== null && task.cost !== undefined && task.cost > 0;
  
  if (hadOriginalCost) {
    // 如果原本有 cost，保持原有的持续时间
    updateData.cost = task.cost;
  }
  // 如果原本没有 cost，不设置 cost（保持为 undefined）

  return {
    updateData,
    shouldRevert: false,
    message: '任务时间已更新'
  };
};

/**
 * 处理任务调整大小（修改持续时间）
 * @param task 原始任务
 * @param newStartTime 新的开始时间
 * @param newEndTime 新的结束时间
 * @returns 拖拽处理结果
 */
export const handleTaskResize = (task: Task, newStartTime: Date, newEndTime: Date): DragResult => {
  // 计算新的持续时间
  const newCost = differenceInSeconds(newEndTime, newStartTime);
  
  if (newCost <= 0) {
    return {
      updateData: { id: task.id },
      shouldRevert: true,
      message: '任务持续时间必须大于0'
    };
  }

  const updateData: UpdateTaskData = {
    id: task.id,
    cost: newCost, // 调整大小时总是更新 cost
  };

  // 如果开始时间也发生了变化，同时更新开始时间
  if (task.startAt) {
    const originalStartTime = new Date(task.startAt);
    if (originalStartTime.getTime() !== newStartTime.getTime()) {
      updateData.startAt = newStartTime.toISOString();
    }
  }

  return {
    updateData,
    shouldRevert: false,
    message: '任务时长已更新'
  };
};

/**
 * 处理任务拖拽到新的时间位置（用于时间轴）
 * @param task 原始任务
 * @param newStartHour 新的开始小时（浮点数，如 14.5 表示 14:30）
 * @returns 拖拽处理结果
 */
export const handleTimelineDrag = (task: Task, newStartHour: number): DragResult => {
  if (!task.startAt) {
    return {
      updateData: { id: task.id },
      shouldRevert: true,
      message: '无法拖拽没有开始时间的任务'
    };
  }

  // 基于原始日期创建新的开始时间
  const originalDate = new Date(task.startAt);
  const newStartTime = new Date(originalDate);
  
  // 设置新的小时和分钟
  const hours = Math.floor(newStartHour);
  const minutes = Math.round((newStartHour - hours) * 60);
  
  newStartTime.setHours(hours, minutes, 0, 0);

  return handleTaskDrag(task, newStartTime);
};

/**
 * 处理任务在时间轴上的调整大小
 * @param task 原始任务
 * @param newStartHour 新的开始小时
 * @param newEndHour 新的结束小时
 * @returns 拖拽处理结果
 */
export const handleTimelineResize = (task: Task, newStartHour: number, newEndHour: number): DragResult => {
  if (!task.startAt) {
    return {
      updateData: { id: task.id },
      shouldRevert: true,
      message: '无法调整没有开始时间的任务'
    };
  }

  if (newEndHour <= newStartHour) {
    return {
      updateData: { id: task.id },
      shouldRevert: true,
      message: '结束时间必须晚于开始时间'
    };
  }

  // 基于原始日期创建新的时间
  const originalDate = new Date(task.startAt);
  
  const newStartTime = new Date(originalDate);
  const startHours = Math.floor(newStartHour);
  const startMinutes = Math.round((newStartHour - startHours) * 60);
  newStartTime.setHours(startHours, startMinutes, 0, 0);
  
  const newEndTime = new Date(originalDate);
  const endHours = Math.floor(newEndHour);
  const endMinutes = Math.round((newEndHour - endHours) * 60);
  newEndTime.setHours(endHours, endMinutes, 0, 0);

  return handleTaskResize(task, newStartTime, newEndTime);
};

/**
 * 验证拖拽操作是否有效
 * @param task 任务
 * @param dragType 拖拽类型
 * @returns 是否可以拖拽
 */
export const canDragTask = (task: Task, dragType: 'move' | 'resize'): boolean => {
  // 移动操作：需要有开始时间
  if (dragType === 'move') {
    return !!task.startAt;
  }

  // 调整大小操作：需要有开始时间
  if (dragType === 'resize') {
    return !!task.startAt;
  }

  return false;
};
